import { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PRODUCT_CAROUSEL } from "../data/site";
import { useReveal } from "../hooks/useReveal";

export default function ProductCarousel() {
    const [idx, setIdx] = useState(0);
    const ref = useReveal();
    const pausedRef = useRef(false);

    useEffect(() => {
        const t = setInterval(() => {
            if (!pausedRef.current) {
                setIdx((i) => (i + 1) % PRODUCT_CAROUSEL.length);
            }
        }, 3000);
        return () => clearInterval(t);
    }, []);

    const go = (dir) =>
        setIdx(
            (i) =>
                (i + dir + PRODUCT_CAROUSEL.length) % PRODUCT_CAROUSEL.length
        );

    return (
        <section
            id="carousel"
            data-testid="product-carousel-section"
            className="bg-white py-20 sm:py-28"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                <div ref={ref} className="reveal mb-10 flex items-end justify-between">
                    <div>
                        <span className="text-xs uppercase tracking-[0.22em] text-brand-red">
                            Showcase
                        </span>
                        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-5xl">
                            Field to Freight.
                        </h2>
                        <p className="mt-3 max-w-xl text-ink-muted">
                            A cinematic look at the spices we grow, grade, and
                            ship — captured at every stage of our supply chain.
                        </p>
                    </div>
                    <div className="hidden items-center gap-2 md:flex">
                        <button
                            data-testid="carousel-prev"
                            onClick={() => go(-1)}
                            aria-label="Previous"
                            className="grid h-11 w-11 place-items-center rounded-full border border-hairline bg-white text-ink transition-colors hover:bg-black/5"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            data-testid="carousel-next"
                            onClick={() => go(1)}
                            aria-label="Next"
                            className="grid h-11 w-11 place-items-center rounded-full border border-hairline bg-white text-ink transition-colors hover:bg-black/5"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div
                    onMouseEnter={() => (pausedRef.current = true)}
                    onMouseLeave={() => (pausedRef.current = false)}
                    className="relative overflow-hidden rounded-3xl bg-black aspect-[16/9] max-md:aspect-[3/4]"
                >
                    {PRODUCT_CAROUSEL.map((src, i) => (
                        <img
                            key={src + i}
                            src={src}
                            loading={i === 0 ? "eager" : "lazy"}
                            alt={`Spice export image ${i + 1}`}
                            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-out ${
                                i === idx ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    ))}

                    <button
                        data-testid="carousel-prev-mobile"
                        onClick={() => go(-1)}
                        aria-label="Previous"
                        className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-ink shadow md:hidden"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        data-testid="carousel-next-mobile"
                        onClick={() => go(1)}
                        aria-label="Next"
                        className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-ink shadow md:hidden"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>

                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
                        {PRODUCT_CAROUSEL.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                data-testid={`carousel-dot-${i}`}
                                aria-label={`Go to slide ${i + 1}`}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === idx
                                        ? "w-7 bg-white"
                                        : "w-1.5 bg-white/50 hover:bg-white/80"
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
